// v2 API service layer.
// One object per domain; methods return axios promises (unwrapped).

import api from './client';

/**
 * Authenticated file download helper.
 *
 * The JWT lives in localStorage and is attached to requests via the axios
 * interceptor in ./client. A plain <a href> or window.open won't carry the
 * Authorization header, so those routes 401.
 *
 * This fetches the file through axios (interceptor adds the token), turns
 * it into a blob URL, and clicks an invisible <a download> to trigger the
 * browser save dialog. Falls back to a best-effort filename if the server
 * didn't set Content-Disposition.
 */
export async function downloadAuthed(path, fallbackFilename = 'download') {
  const res = await api.get(path, { responseType: 'blob' });
  // Pull filename from Content-Disposition when present
  let filename = fallbackFilename;
  const cd = res.headers['content-disposition'];
  if (cd) {
    const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
    if (m && m[1]) filename = decodeURIComponent(m[1]);
  }
  const url = URL.createObjectURL(res.data);
  try {
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.style.display = 'none';
    document.body.appendChild(a); a.click(); a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  }
}

// ----- Auth -----
export const authApi = {
  login: (body) => api.post('/auth/login', body),
  me: () => api.get('/auth/me'),
  changePassword: (body) => api.post('/auth/change-password', body),
};

// ----- Intake (MBO uploads + intake batches) -----
export const intakeApi = {
  list: (params) => api.get('/intake', { params }),
  get: (id) => api.get(`/intake/${id}`),
  upload: (formData) =>
    api.post('/intake/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  assignToUser: (batchId, body) => api.post(`/intake/${batchId}/assign`, body),
  downloadOrders: (batchId) =>
    downloadAuthed(`/intake/${batchId}/orders.xlsx`, 'orders.xlsx'),
};

// ----- Orders (v2 — state via handoff, reads via order/timeline/items) -----
export const orderApi = {
  get: (id) => api.get(`/orders/${id}`),
  setReshipReason: (id, reshipReason) =>
    api.patch(`/orders/${id}/reship-reason`, { reshipReason }),
  listByOwner: (params) => api.get('/orders/by-owner', { params }),
  listActive: (params) => api.get('/orders/active', { params }),
  exportCsvUrl: (qs) => `/api/orders/export.csv${qs ? `?${qs}` : ''}`,
  getTimeline: (id) => api.get(`/orders/${id}/timeline`),
  getItems: (id) => api.get(`/orders/${id}/items`),
  myQueue: () => api.get('/orders/my-queue'),
  myQueueUnreadCount: () => api.get('/orders/my-queue/unread-count'),
  remind: (id, note) => api.post(`/orders/${id}/remind`, { note }),
  onHold: () => api.get('/orders/on-hold'),
  handoff: (id, body) => api.post(`/orders/${id}/handoff`, body),
  submitForReview: (id, body) => api.post(`/orders/${id}/submit-for-review`, body),
  bulkSubmitForReview: (body) => api.post('/orders/bulk/submit-for-review', body),
  bulkSendBack: (body) => api.post('/orders/bulk/send-back', body),
  bulkReassignVendor: (body) => api.post('/orders/bulk/reassign-vendor', body),
  approveForDispatch: (id) => api.post(`/orders/${id}/approve`),
  sendBack: (id, body) => api.post(`/orders/${id}/send-back`, body),
  substituteProduct: (itemId, body) => api.post(`/orders/items/${itemId}/substitute`, body),
  splitShipment: (shipmentId, body) => api.post(`/orders/shipments/${shipmentId}/split`, body),
  getSplitPlan: (id) => api.get(`/orders/${id}/split-plan`),
  editSplit: (id, body) => api.post(`/orders/${id}/edit-split`, body),
  rescheduleShipment: (shipmentId, body) => api.post(`/orders/shipments/${shipmentId}/reschedule`, body),
  deferOrder: (id, body) => api.post(`/orders/${id}/defer`, body),
  bulkDefer: (body) => api.post('/orders/bulk/defer', body),
  clarifyVirtualProduct: (id, body) => api.post(`/orders/${id}/clarify-virtual`, body),
  linkReship: (id, body) => api.post(`/orders/${id}/link-reship`, body),
  linkReshipExternal: (id, body) => api.post(`/orders/${id}/link-reship-external`, body),
  availableHandoffs: (fromTeam) => api.get(`/handoff-reasons/available/${fromTeam}`),
  // Partial shipment handling (Kalpana 2026-05-26 sign-off)
  recordPartialShipment: (orderId, body) => api.post(`/orders/${orderId}/partial-shipment`, body),
  reverseCancellation: (itemId, body) => api.post(`/orders/items/${itemId}/reverse-cancellation`, body),
  recordItemRefund: (itemId, body) => api.post(`/orders/items/${itemId}/refund`, body),
  undoItemRefund: (itemId, body) => api.post(`/orders/items/${itemId}/undo-refund`, body),
  // Ops product-change flow (DMS-20260702 / issue #1)
  requestProductChange: (id, body) => api.post(`/orders/${id}/request-product-change`, body),
  completeProductChange: (id, body) => api.post(`/orders/${id}/complete-product-change`, body),
  addItem: (id, body) => api.post(`/orders/${id}/items`, body),
  editItem: (itemId, body) => api.patch(`/orders/items/${itemId}`, body),
  removeItem: (itemId) => api.delete(`/orders/items/${itemId}`),
};

// ----- Dispatch -----
export const dispatchApi = {
  listReady: (params) => api.get('/dispatch/ready', { params }),
  listScheduledSplits: (params) => api.get('/dispatch/scheduled-splits', { params }),
  listPendingTracking: () => api.get('/dispatch/pending-tracking'),
  allocate: (body) => api.post('/dispatch/allocate', body),
  run: (body) => api.post('/dispatch/run', body),
  list: (params) => api.get('/dispatch', { params }),
  get: (id) => api.get(`/dispatch/${id}`),
  generateXlsx: (id) => api.post(`/dispatch/${id}/generate-xlsx`),
  downloadXlsxUrl: (id) => `/api/dispatch/${id}/download-xlsx`,
  downloadXlsxNoOrderIdUrl: (id) => `/api/dispatch/${id}/download-xlsx-no-order-id`,
  markEmailed: (id) => api.post(`/dispatch/${id}/mark-emailed`),
  recoverMissedScheduled: () => api.post('/dispatch/recover-missed-scheduled'),
  autoPromoteReady: () => api.post('/dispatch/auto-promote-ready'),
  missedScheduledSummary: () => api.get('/dispatch/missed-scheduled-summary'),
};

// ----- Admin Orders (address approval, etc.) -----
export const adminOrderApi = {
  getAddressPending: (orderId) =>
    api.get(`/admin/orders/${orderId}/address-pending`),
  approveAddressUpdate: (orderId, body) =>
    api.post(`/admin/orders/${orderId}/approve-address-update`, body),
  rejectAddressUpdate: (orderId, body) =>
    api.post(`/admin/orders/${orderId}/reject-address-update`, body),
};

// ----- Tracking -----
export const trackingApi = {
  upload: (formData) =>
    api.post('/tracking/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  shareWithOps: (body) => api.post('/tracking/share-with-ops', body),
  closeOrder: (body) => api.post('/tracking/close-order', body),
  mboExportUrl: (force) =>
    `/api/tracking/mbo-export${force ? '?force_all=true' : ''}`,
  markExportedToMbo: (body) => api.post('/tracking/mbo-export/mark-sent', body),
  updateRevised: (shipmentId, body) =>
    api.post(`/tracking/shipments/${shipmentId}/revised`, body),
  inTransit: () => api.get('/tracking/in-transit'),
  mboExportPreview: (force) => api.get(`/tracking/mbo-export${force ? '?force_all=true' : ''}`, { responseType: 'text' }),

  // Carrier tracking (India Post via CEPT)
  carrierPoll: () => api.post('/tracking/carrier/poll'),
  carrierRefresh: (shipmentId) => api.post(`/tracking/carrier/refresh/${shipmentId}`),
  markDelivered: (shipmentId, body) => api.post(`/tracking/shipments/${shipmentId}/mark-delivered`, body),
  carrierLookup: (trackingNumber) => api.get(`/tracking/carrier/lookup/${trackingNumber}`),
  carrierStatus: () => api.get('/tracking/carrier/status'),
  carrierDashboard: (params) => api.get('/tracking/carrier/dashboard', { params }),
  addParcel: (orderId, body) => api.post(`/tracking/orders/${orderId}/parcels`, body),
};

// ----- Ops & Cancellation & Hold & Vendor Response -----
// Monthly IDM Sales Report (#25)
export const salesReportApi = {
  get: (params) => api.get('/reports/sales-report', { params }),
  download: ({ year, month }) =>
    downloadAuthed(`/reports/sales-report/export.xlsx?year=${year}&month=${month}`,
      'sales-report.xlsx'),
};

export const opsApi = {
  queue: (params) => api.get('/ops/queue', { params }),
  escalateToCC: (orderId, body) => api.post(`/ops/${orderId}/escalate-cc`, body),
  customerResponded: (orderId, body) =>
    api.post(`/ops/${orderId}/customer-responded`, body),
};

// Admin address-correction review (DMS-20260508)
export const adminAddressApi = {
  getPending: (orderId) => api.get(`/admin/orders/${orderId}/address-pending`),
  approve: (orderId, body) => api.post(`/admin/orders/${orderId}/approve-address-update`, body || {}),
  reject:  (orderId, body) => api.post(`/admin/orders/${orderId}/reject-address-update`, body || {}),
};

export const cancellationApi = {
  requestByOps: (orderId, body) =>
    api.post(`/cancellations/${orderId}/request-by-ops`, body),
  requestByUserVendorCannotFulfil: (orderId, body) =>
    api.post(`/cancellations/${orderId}/request-by-user-vendor-cannot-fulfil`, body),
  confirm: (orderId) => api.post(`/cancellations/${orderId}/confirm`),
  fail: (orderId, body) => api.post(`/cancellations/${orderId}/fail`, body),
  close: (orderId) => api.post(`/cancellations/${orderId}/close`),
  customerPaidAfterDelivery: (orderId) =>
    api.post(`/cancellations/${orderId}/customer-paid-after-delivery`),
  markCustomerTroublesome: (orderId, body) =>
    api.post(`/cancellations/${orderId}/mark-customer-troublesome`, body),
  updateRefundPaymentStatus: (orderId, body) =>
    api.patch(`/cancellations/${orderId}/refund-payment-status`, body),
};

export const holdApi = {
  request: (orderId, body) => api.post(`/holds/${orderId}/request`, body),
  release: (orderId) => api.post(`/holds/${orderId}/release`),
  markReminderSent: (orderId) =>
    api.post(`/holds/${orderId}/reminder-sent`),
  timers: () => api.get('/holds/timers'),
};

export const vendorResponseApi = {
  record: (orderId, body) => api.post(`/vendor-responses/${orderId}/record`, body),
  customerApprovedAlt: (orderId, body) =>
    api.post(`/vendor-responses/${orderId}/customer-approved-alt`, body),
  customerRejectedAlt: (orderId, body) =>
    api.post(`/vendor-responses/${orderId}/customer-rejected-alt`, body),
  shipPartialHoldOos: (orderId, body) =>
    api.post(`/vendor-responses/${orderId}/ship-partial-hold-oos`, body),
  shipPartialCancelOos: (orderId, body) =>
    api.post(`/vendor-responses/${orderId}/ship-partial-cancel-oos`, body),
  presentMultiAlt: (orderId, body) =>
    api.post(`/vendor-responses/${orderId}/present-multi-alt`, body),
  reshipReassignVendor: (orderId, body) =>
    api.post(`/vendor-responses/${orderId}/reship-reassign-vendor`, body),
};

// ----- Super Admin -----
export const adminActivityLogApi = {
  list: (params) => api.get('/admin/activity-logs', { params }),
  get: (id) => api.get(`/admin/activity-logs/${id}`),
  exportCsvUrl: (qs) => `/api/admin/activity-logs/export.csv${qs ? `?${qs}` : ''}`,
  userActivity: (userId, params) =>
    api.get(`/admin/activity-logs/user/${userId}`, { params }),
  entityViewers: (entityType, entityId, params) =>
    api.get(`/admin/activity-logs/entity/${entityType}/${entityId}/viewers`, { params }),
  fileDownloads: (params) => api.get('/admin/activity-logs/file-downloads', { params }),
  failedLogins: (params) => api.get('/admin/activity-logs/failed-logins', { params }),
  nearTimestamp: (params) => api.get('/admin/activity-logs/near-timestamp', { params }),
  dispatchSensitive: (userId, params) =>
    api.get(`/admin/activity-logs/dispatch-sensitive/${userId}`, { params }),
};

export const adminStatusReportApi = {
  summary: (from, to) => api.get('/admin/status-report/summary', { params: { from, to } }),
  orders: (from, to, type, key) =>
    api.get('/admin/status-report/orders', { params: { from, to, type, key } }),
  exportCsvUrl: (from, to) => `/api/admin/status-report/export.csv?from=${from}&to=${to}`,
};

export const validationFlagApi = {
  forOrder: (orderId) => api.get(`/validation-flags/order/${orderId}`),
  pending: () => api.get('/validation-flags/pending'),
  approve: (id, body) => api.post(`/validation-flags/${id}/approve`, body),
  reject: (id, body) => api.post(`/validation-flags/${id}/reject`, body),
  skip: (id, body) => api.post(`/validation-flags/${id}/skip`, body),
  edit: (id, body) => api.put(`/validation-flags/${id}`, body),
  bulkApprove: (body) => api.post('/validation-flags/bulk/approve', body),
  bulkReject: (body) => api.post('/validation-flags/bulk/reject', body),
  approveAllOnOrder: (orderId) => api.post(`/validation-flags/order/${orderId}/approve-all`),
};

export const shipperValidationApi = {
  list: (params) => api.get('/admin/shipper-validation', { params }),
  add: (body) => api.post('/admin/shipper-validation', body),
  remove: (id) => api.delete(`/admin/shipper-validation/${id}`),
  bulkUpload: (formData) =>
    api.post('/admin/shipper-validation/bulk', formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }),
  sampleUrl: () => `/api/admin/shipper-validation/sample.csv`,
  exportUrl: () => `/api/admin/shipper-validation/export.csv`,
};

// Temporary backfill panel (reship reasons + unmapped lines)
export const adminCleanupApi = {
  reshipsMissingReason: () => api.get('/admin/cleanup/reships-missing-reason'),
  unmappedLines: () => api.get('/admin/cleanup/unmapped-lines'),
  mapProduct: (body) => api.post('/admin/cleanup/map-product', body),
  duplicateVendorProducts: () => api.get('/admin/cleanup/duplicate-vendor-products'),
  mergeVendorProducts: (body) => api.post('/admin/cleanup/merge-vendor-products', body),
  splitVendorProducts: (body) => api.post('/admin/cleanup/split-vendor-products', body),
};

export const productMappingApi = {
  list: (params) => api.get('/admin/product-mapping', { params }),
  add: (body) => api.post('/admin/product-mapping', body),
  confirmAlias: (body) => api.post('/admin/product-mapping/confirm-alias', body),
  update: (id, body) => api.put(`/admin/product-mapping/${id}`, body),
  remove: (id) => api.delete(`/admin/product-mapping/${id}`),
  bulkUpload: (formData) =>
    api.post('/admin/product-mapping/bulk', formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }),
  bulkUploadMasterFromMbo: (formData) =>
    api.post('/admin/product-mapping/bulk-master-from-mbo', formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }),
  sampleUrl: () => `/api/admin/product-mapping/sample.csv`,
  sampleMboXlsxUrl: () => `/api/admin/product-mapping/sample-mbo.xlsx`,
  exportUrl: () => `/api/admin/product-mapping/export.csv`,
};

export const adminDataQualityApi = {
  summary: () => api.get('/admin/data-quality/summary'),
  blockedCustomers: (params) => api.get('/admin/data-quality/blocked-customers', { params }),
  virtualProducts: (params) => api.get('/admin/data-quality/virtual-products', { params }),
  flagged: (params) => api.get('/admin/data-quality/flagged', { params }),
  raiseToOps: (body) => api.post('/admin/data-quality/flagged/raise-to-ops', body),
};

export const customerApi = {
  clearTroublesome: (id, body) => api.post(`/customers/${id}/clear-troublesome`, body),
  approveBlockedOrder: (orderId) => api.post(`/orders/${orderId}/approve-blocked`),
};

// ----- Users & Vendors (legacy-compatible) -----
export const userApi = {
  list: (params) => api.get('/users', { params }),
  assignable: () => api.get('/users/assignable'),
  roles: () => api.get('/users/roles'),
  get: (id) => api.get(`/users/${id}`),
  create: (body) => api.post('/users', body),
  update: (id, body) => api.put(`/users/${id}`, body),
  deactivate: (id) => api.post(`/users/${id}/deactivate`),
  reactivate: (id) => api.post(`/users/${id}/reactivate`),
  resetPassword: (id, body) => api.post(`/users/${id}/reset-password`, body),
};

export const vendorApi = {
  list: (params) => api.get('/vendors', { params }),
  get: (id) => api.get(`/vendors/${id}`),
  create: (body) => api.post('/vendors', body),
  update: (id, body) => api.put(`/vendors/${id}`, body),
  products: (id, params) => api.get(`/vendors/${id}/products`, { params }),
  addProduct: (id, body) => api.post(`/vendors/${id}/products`, body),
  setProductAvailability: (productId, body) =>
    api.put(`/vendors/products/${productId}/availability`, body),
  setDefault: (id, isDefault) => api.put(`/vendors/${id}/default`, { isDefault }),
  setPackageShipping: (id, amount) => api.put(`/vendors/${id}/package-shipping`, { amount }),
  setProductPreferred: (productId, isPreferred) =>
    api.put(`/vendors/products/${productId}/preferred`, { isPreferred }),
  addPricing: (productId, body) => api.post(`/vendors/products/${productId}/pricing`, body),
  pricingHistory: (productId) => api.get(`/vendors/products/${productId}/pricing`),
  pricingSampleCsvUrl: '/api/vendors/pricing-sample.csv',
  bulkUploadPricing: (vendorId, formData) =>
    api.post(`/vendors/${vendorId}/pricing/bulk`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  pricingImports: (vendorId, params) =>
    api.get(`/vendors/${vendorId}/pricing/imports`, { params }),
  reshipPolicies: (vendorId) => api.get(`/vendors/${vendorId}/reship-policies`),
  reshipPolicyHistory: (vendorId) => api.get(`/vendors/${vendorId}/reship-policies/history`),
  updateReshipPolicy: (vendorId, reason, body) =>
    api.put(`/vendors/${vendorId}/reship-policies/${reason}`, body),
  updateReshipOverrides: (vendorId, body) =>
    api.put(`/vendors/${vendorId}/reship-overrides`, body),
  shippingRates: (vendorId) => api.get(`/vendors/${vendorId}/shipping-rates`),
  shippingRateHistory: (vendorId) => api.get(`/vendors/${vendorId}/shipping-rates/history`),
  shippingImports: (vendorId) => api.get(`/vendors/${vendorId}/shipping-rates/imports`),
  importShippingRates: (vendorId, formData) =>
    api.post(`/vendors/${vendorId}/shipping-rates/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
};

// Owner Cockpit — super_admin landing
export const cockpitApi = {
  pulse: (params) => api.get('/reports/cockpit', { params }),
  stuck: (params) => api.get('/reports/cockpit/stuck', { params }),
  drill: (params) => api.get('/reports/cockpit/drill', { params }),
  digest: (params) => api.get('/reports/cockpit/digest', { params }),
  analytics: (params) => api.get('/reports/analytics', { params }),
};

// Money at Risk — super_admin exposure view
export const moneyAtRiskApi = {
  build: (params) => api.get('/reports/money-at-risk', { params }),
};

// Vendor Follow-up — dispatch_admin chase board
export const vendorFollowupApi = {
  build: () => api.get('/reports/vendor-followup'),
};

// Tracking Health — dispatch_admin parcel-intervention board
export const trackingHealthApi = {
  build: () => api.get('/reports/tracking-health'),
};

// Dispatch Desk — dispatch_admin's own dashboard
export const dispatchDeskApi = {
  build: () => api.get('/reports/dispatch-desk'),
};

// Ops Desk — ops team's own dashboard (also viewed by dispatch_admin)
export const opsDeskApi = {
  build: () => api.get('/reports/ops-desk'),
};

// Vendor settlements — super_admin payment review & sign-off
export const settlementsApi = {
  review: (params) => api.get('/admin/settlements/review', { params }),
  approve: (data) => api.post('/admin/settlements/approve', data),
  list: () => api.get('/admin/settlements'),
  remove: (id) => api.delete(`/admin/settlements/${id}`),
};

// AI Assistant — super_admin only chat over live operational data
export const aiChatApi = {
  send: (data) => api.post('/admin/ai-chat', data),
  latest: () => api.get('/admin/ai-chat/latest'),
  conversations: () => api.get('/admin/ai-chat/conversations'),
  conversation: (id) => api.get(`/admin/ai-chat/conversation/${id}`),
  usage: () => api.get('/admin/ai-chat/usage'),
  resolveOrder: (ext) => api.get(`/orders/resolve-external/${encodeURIComponent(ext)}`),
};

// Vendor Payables — super_admin reports
export const vendorPayablesApi = {
  build: (params) => api.get('/reports/vendor-payables', { params }),
  exportCsvUrl: (from, to, vendorId) => {
    const qs = new URLSearchParams({ from, to, ...(vendorId ? { vendorId } : {}) }).toString();
    return `/api/reports/vendor-payables/export.csv?${qs}`;
  },
};

// ----- Comments -----
export const commentApi = {
  byOrder: (orderId) => api.get(`/comments/order/${orderId}`),
  create: (body) => api.post('/comments', body),
};

// ----- Notifications -----
export const notificationApi = {
  list: (params) => api.get('/notifications', { params }),
  // Server uses PUT (idempotent) — see server/src/routes/notifications.js.
  // The previous POST + /mark-all-read was silently 404ing in the Notif
  // center, so "Mark all read" looked clickable but did nothing.
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
};

// Vendor product mapping queue (Phase 5 of the mapping bridge).
export const vendorMappingQueueApi = {
  list: (params) => api.get('/admin/vendor-mapping', { params }),
  counts: (params) => api.get('/admin/vendor-mapping/counts', { params }),
  link: (id, masterId) => api.post(`/admin/vendor-mapping/${id}/link`, { masterId }),
  createMaster: (id, body) => api.post(`/admin/vendor-mapping/${id}/create-master`, body),
  skip: (id) => api.post(`/admin/vendor-mapping/${id}/skip`),
  editVendorProductName: (id, newName) =>
    api.put(`/admin/vendor-mapping/${id}/vendor-product-name`, { newName }),
  unpriced: () => api.get('/admin/vendor-mapping/unpriced'),
  aiStatus: () => api.get('/admin/vendor-mapping/ai/status'),
  aiRunBacklog: (body) => api.post('/admin/vendor-mapping/ai/run-backlog', body || {}),
  aiBulkApprove: (body) => api.post('/admin/vendor-mapping/ai/bulk-approve', body || {}),
  aiApprove: (id) => api.post(`/admin/vendor-mapping/${id}/ai-approve`),
  aiReject: (id) => api.post(`/admin/vendor-mapping/${id}/ai-reject`),
};
